"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Context = void 0;
exports.copyValue = copyValue;
exports.simplifyMessages = simplifyMessages;
const utils_1 = require("./utils");
class Context {
    root;
    path;
    value;
    messages = [];
    warnings = [];
    errors = [];
    transferableData = null;
    static fromValue(value) {
        return new Context({ root: value, path: [], value });
    }
    constructor({ root, path, value }) {
        this.root = root;
        this.path = path;
        this.value = value;
    }
    clone() {
        const { root, path, value } = this;
        return new Context({ root, path, value });
    }
    merge({ path, value }) {
        const { root } = this;
        return new Context({ root, path, value });
    }
    createChild(key) {
        const path = this.path.concat(key);
        const value = this.value[key];
        return this.merge({ path, value });
    }
    mergeLogItems(ctx) {
        if (ctx.messages.length !== 0) {
            this.messages.push(...ctx.messages);
        }
        if (ctx.warnings.length !== 0) {
            this.warnings.push(...ctx.warnings);
        }
        if (ctx.errors.length !== 0) {
            this.errors.push(...ctx.errors);
        }
    }
    toResult() {
        const { value, messages, warnings, errors } = this;
        const result = { value };
        if (messages.length !== 0) {
            result.messages = simplifyMessages(messages);
        }
        if (warnings.length !== 0) {
            result.warnings = warnings;
        }
        if (errors.length !== 0) {
            result.errors = errors;
        }
        return result;
    }
    setValue(value) {
        this.value = copyValue(value);
    }
    addMessageWithPath(details) {
        const { path } = this;
        this.messages.push({ path, details });
    }
    addMessageWithValue(details) {
        this.messages.push({ value: this.value, details });
    }
    addWarning(details) {
        const { path } = this;
        this.warnings.push({ path, details });
    }
    addError(details) {
        const { path } = this;
        this.errors.push({ path, details });
    }
    hasTransferableData() {
        return this.transferableData !== null;
    }
    setTransferableData(data) {
        this.transferableData = data;
    }
    transferData(ctx) {
        const { transferableData: data } = ctx;
        if (data === null) {
            return;
        }
        this.addMessageWithPath(data);
    }
}
exports.Context = Context;
function copyValue(value) {
    if (Array.isArray(value)) {
        return [...value];
    }
    if (typeof value === "object" && value !== null) {
        return { ...value };
    }
    return value;
}
function simplifyMessages(items) {
    const itemsByPath = {};
    const itemsWithValue = [];
    for (const item of items) {
        if ((0, utils_1.isLogItemWithPath)(item)) {
            const key = item.path.join("/");
            if (!(key in itemsByPath)) {
                itemsByPath[key] = [];
            }
            itemsByPath[key].push(item);
        }
        else {
            itemsWithValue.push(item);
        }
    }
    return [
        ...Object.values(itemsByPath).map(items => ({
            ...items[0],
            details: joinMessagesToDetails(items)
        })),
        ...itemsWithValue
    ];
}
function joinMessagesToDetails(items) {
    const details = {};
    for (const item of items) {
        for (const key of Object.keys(item.details)) {
            const { [key]: valueA } = details;
            const { [key]: valueB } = item.details;
            if (Array.isArray(valueA) && Array.isArray(valueB)) {
                details[key] = valueA.concat(valueB);
            }
            else if (typeof valueA === "object" &&
                typeof valueB === "object") {
                details[key] = { ...valueA, ...valueB };
            }
            else {
                details[key] = valueB;
            }
        }
    }
    if (details.interpretations !== undefined) {
        details.interpretations = (0, utils_1.getUniqueInterpretations)(details.interpretations);
    }
    if (details.tags !== undefined) {
        details.tags = (0, utils_1.getUniqueTags)(details.tags);
    }
    return details;
}
